import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner, Modal, Popconfirm, TextField } from "@mapmycustomers/ui";

import {
  getEmailTemplatesForEntityType,
  isEmailTemplateDeleting,
  isEmailTemplateUpdating,
} from "@app/store/email";
import {
  deleteEmailTemplate,
  fetchEmailPreview,
  updateEmailTemplate,
} from "@app/store/email/actions";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import createEmailAssociatedEntity from "@app/util/email/createEmailAssociatedEntity";

import EmailRecipientTypeSelector from "../EmailRecipientTypeSelector";
import EmailView from "../EmailView";

import styles from "./EmailTemplateModal.module.scss";
import messages from "./messages";
import TemplateAttachmentsAlert from "./TemplateAttachmentsAlert";
import TemplatePreviewAsModal from "./TemplatePreviewAsModal";
import TemplateShareModal from "./TemplateShareModal";
import TemplateUsers from "./TemplateUsers";
import TemplateView from "./TemplateView";

type EmailTemplatesModalProps = {
  applyLabel: ReactNode;
  onApply: (template: EmailTemplate) => void;
  onHide: () => void;
  onSelectRecipientId?: (id?: EntitiesSupportedByEmailFeature["id"]) => void;
  recipients?: EntitiesSupportedByEmailFeature[];
  recipientType?: EntityTypesSupportedByEmailFeature;
  selectedRecipientId?: EntitiesSupportedByEmailFeature["id"];
  showPreviewAs?: boolean;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const EmailTemplatesModal: React.FC<EmailTemplatesModalProps> = ({
  applyLabel,
  currentUserId,
  deleting,
  onApply,
  onDeleteEmailTemplate,
  onFetchEmailPreview,
  onHide,
  onSelectRecipientId,
  onUpdateEmailTemplate,
  recipients = [],
  recipientType: initialRecipientType,
  selectedRecipientId,
  showPreviewAs,
  templatesForEntityType,
  updating,
}) => {
  const intl = useIntl();

  const [recipientType, setRecipientType] = useState<EntityTypesSupportedByEmailFeature>(
    initialRecipientType ?? EntityType.COMPANY
  );
  const templates = templatesForEntityType(recipientType);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | undefined>();
  const [deletedTemplateIndex, setDeletedTemplateIndex] = useState<number | undefined>();
  const [editingName, setEditingName] = useState<string | undefined>();
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreview, setTemplatePreview] = useState<EmailPreview | undefined>();
  const [previewAsModalVisible, showPreviewAsModal, hidePreviewAsModal] = useBoolean();
  const [shareModalVisible, showShareModal, hideShareModal] = useBoolean();

  const selectedRecipient = useMemo(
    () =>
      selectedRecipientId
        ? recipients.find((recipient) => recipient.id === selectedRecipientId)
        : undefined,
    [recipients, selectedRecipientId]
  );

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }

    // refresh selected template after templates updated
    const selectedTemplateObj = templates.find((template) => template.id === selectedTemplate.id);
    if (selectedTemplateObj && selectedTemplate !== selectedTemplateObj) {
      setSelectedTemplate(selectedTemplateObj);
      return;
    }

    // refresh selected template after templates deleted
    if (!selectedTemplateObj && deletedTemplateIndex !== undefined) {
      const newSelectedTemplate = templates.length
        ? templates[deletedTemplateIndex + (deletedTemplateIndex === templates.length ? -1 : 1)]
        : undefined;
      setSelectedTemplate(newSelectedTemplate);
      setDeletedTemplateIndex(undefined);
    }
  }, [templates, selectedTemplate, deletedTemplateIndex]);

  useEffect(() => {
    if (selectedRecipientId && selectedTemplate && recipientType) {
      setTemplatePreviewLoading(true);
      onFetchEmailPreview({
        email: {
          associatedEntities: [createEmailAssociatedEntity({ entityId: selectedRecipientId })],
          body: selectedTemplate.body,
          entityType: recipientType,
          isHtml: true,
          logActivity: false,
          subject: selectedTemplate.subject,
          tracking: true,
        },
        onFailure: () => {
          setTemplatePreviewLoading(false);
        },
        onSuccess: (templatePreview) => {
          setTemplatePreview(templatePreview);
          setTemplatePreviewLoading(false);
        },
      });
    }
  }, [recipientType, onFetchEmailPreview, selectedRecipientId, selectedTemplate]);

  const handleDelete = useCallback(() => {
    if (selectedTemplate) {
      setDeletedTemplateIndex(templates.findIndex(({ id }) => id === selectedTemplate.id));
      onDeleteEmailTemplate({ templateId: selectedTemplate.id });
    }
  }, [onDeleteEmailTemplate, selectedTemplate, templates]);

  const handleRename = useCallback(() => {
    if (selectedTemplate && editingName) {
      onUpdateEmailTemplate({
        callback: (template: EmailTemplate) => {
          setEditingName(undefined);
          setSelectedTemplate(template);
        },
        template: { ...selectedTemplate, name: editingName },
      });
    }
  }, [editingName, selectedTemplate, setEditingName, setSelectedTemplate, onUpdateEmailTemplate]);

  const handleExitPreview = useCallback(() => {
    onSelectRecipientId?.();
    setTemplatePreview(undefined);
  }, [onSelectRecipientId]);

  useEffect(() => {
    if (recipients.length) {
      onSelectRecipientId?.(recipients[0]?.id);
    }
  }, [onSelectRecipientId, recipients]);

  useEffect(() => {
    if (
      (!selectedTemplate && templates.length) ||
      (selectedTemplate && !templates.find((template) => template.id === selectedTemplate.id))
    ) {
      setSelectedTemplate(templates[0]);
    }
  }, [selectedTemplate, templates]);

  useEffect(() => {
    setEditingName(undefined);
    // use need to drop name by selected template changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const actionItems = useMemo(() => {
    const shareDisabled = selectedTemplate?.userId !== currentUserId;
    const previewAsDisabled = !recipients.length;
    return [
      ...(templatePreview
        ? [
            {
              key: "exitPreview",
              label: intl.formatMessage(messages.exitPreview),
              onClick: handleExitPreview,
            },
          ]
        : []),
      ...(showPreviewAs
        ? [
            {
              disabled: previewAsDisabled,
              key: "previewAs",
              label: (
                <Tooltip
                  title={
                    previewAsDisabled
                      ? intl.formatMessage(messages.previewAsDisabledTooltip)
                      : undefined
                  }
                >
                  {intl.formatMessage(messages.previewAs)}
                </Tooltip>
              ),
              onClick: showPreviewAsModal,
            },
          ]
        : []),
      {
        key: "rename",
        label: intl.formatMessage(messages.rename),
        onClick: () => setEditingName(selectedTemplate?.name),
      },
      {
        disabled: shareDisabled,
        key: "share",
        label: (
          <Tooltip
            title={
              shareDisabled ? intl.formatMessage(messages.shareDisabledDescription) : undefined
            }
          >
            <span>{intl.formatMessage(messages.share)}</span>
          </Tooltip>
        ),
        onClick: showShareModal,
      },
      {
        key: "delete",
        label: (
          <Popconfirm
            cancelText={intl.formatMessage(messages.deleteConfirmationCancel)}
            danger
            description={intl.formatMessage(messages.deleteConfirmationText)}
            okButtonProps={{ disabled: deleting, loading: deleting }}
            okText={intl.formatMessage(messages.deleteConfirmationOk)}
            onConfirm={handleDelete}
            overlayClassName={styles.confirmation}
            placement="left"
            title
          >
            <span onClick={stopEvents}>{intl.formatMessage(messages.delete)}</span>
          </Popconfirm>
        ),
      },
    ];
  }, [
    currentUserId,
    deleting,
    handleDelete,
    intl,
    recipients,
    showPreviewAsModal,
    showShareModal,
    setEditingName,
    selectedTemplate,
    showPreviewAs,
    handleExitPreview,
    templatePreview,
  ]);

  const handleApply = useCallback(() => {
    if (selectedTemplate) {
      onApply(selectedTemplate);
    }
  }, [onApply, selectedTemplate]);

  const renderTemplateView = () => {
    if (templatePreviewLoading) {
      return <LoadingSpinner />;
    }

    if (templatePreview) {
      return (
        <EmailView
          body={templatePreview.html.body}
          recipient={selectedRecipient}
          subject={templatePreview.html.subject}
        />
      );
    }

    if (selectedTemplate) {
      return <TemplateView template={selectedTemplate} />;
    }

    return null;
  };

  const renderActionItemsDropdown = () => {
    return (
      <Dropdown
        menu={{ items: actionItems }}
        overlayClassName={styles.templateMenu}
        trigger={["click"]}
      >
        <FontAwesomeIcon className={styles.moreIcon} icon={faEllipsisH} />
      </Dropdown>
    );
  };

  return (
    <>
      <Modal
        centered
        className={styles.modal}
        footer={null}
        onCancel={onHide}
        open
        scrollable
        title={
          <>
            <span>{intl.formatMessage(messages.emailTemplates)}</span>
            <EmailRecipientTypeSelector
              disabled={!!initialRecipientType}
              onChange={setRecipientType}
              value={recipientType}
            />
          </>
        }
        width="clamp(300px, 80vw, 950px)"
      >
        <div className={styles.container}>
          <div className={styles.templateList}>
            {templates.map((template) => (
              <div
                className={cn(styles.templateListItem, {
                  [styles.templateSelected]: template.id === selectedTemplate?.id,
                })}
                key={template.id}
                onClick={() => setSelectedTemplate(template)}
              >
                <span className={styles.templateListItemName}>{template.name}</span>
                {template.userIds.length > 0 && (
                  <span className={styles.templateListItemSharedIcon}>
                    <FontAwesomeIcon className={styles.templateSharedIcon} icon={faUsers} />
                  </span>
                )}
              </div>
            ))}
          </div>
          {selectedTemplate && (
            <div className={styles.templatePreview}>
              {templatePreview && (
                <div className={styles.templateModeContainer}>
                  <Tag className={styles.templateTag}>
                    {intl.formatMessage(messages.previewModeTagLabel)}
                  </Tag>
                  {renderActionItemsDropdown()}
                </div>
              )}
              <div className={styles.templateHeader}>
                {editingName === undefined ? (
                  <div
                    className={styles.templateName}
                    onClick={() => setEditingName(selectedTemplate.name)}
                  >
                    {selectedTemplate.name}
                  </div>
                ) : (
                  <div className={styles.templateNameEdit}>
                    {/* TODO: Use Editable Text component */}
                    <TextField
                      className={styles.templateNameEditField}
                      onChange={setEditingName}
                      value={editingName}
                    />
                    <Row gutter={layout.spacerS}>
                      <Col>
                        <Button
                          disabled={updating}
                          iconOnly
                          loading={updating}
                          onClick={() => setEditingName(undefined)}
                          size="small"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          disabled={updating || editingName.trim() === ""}
                          iconOnly
                          loading={updating}
                          onClick={handleRename}
                          size="small"
                          type="primary"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </Button>
                      </Col>
                    </Row>
                  </div>
                )}
                {!templatePreview && renderActionItemsDropdown()}
              </div>
              <TemplateUsers template={selectedTemplate} />
              <div className={styles.templateView}>{renderTemplateView()}</div>
              <div className={styles.templateFooter}>
                <TemplateAttachmentsAlert />
              </div>
            </div>
          )}
        </div>
        <Row className={styles.footer} justify="end">
          <Col>
            <Button disabled={!selectedTemplate} onClick={handleApply} type="primary">
              {applyLabel}
            </Button>
          </Col>
        </Row>
      </Modal>
      {previewAsModalVisible && (
        <TemplatePreviewAsModal
          onHide={hidePreviewAsModal}
          onSelectRecipientId={onSelectRecipientId}
          recipients={recipients}
          selectedRecipientId={selectedRecipientId}
        />
      )}
      {shareModalVisible && selectedTemplate && (
        <TemplateShareModal onHide={hideShareModal} template={selectedTemplate} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  deleting: isEmailTemplateDeleting(state),
  templatesForEntityType: getEmailTemplatesForEntityType(state),
  updating: isEmailTemplateUpdating(state),
});

const mapDispatchToProps = {
  onDeleteEmailTemplate: deleteEmailTemplate.request,
  onFetchEmailPreview: fetchEmailPreview,
  onUpdateEmailTemplate: updateEmailTemplate.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailTemplatesModal);
